/**
 * DIME cloud web-api client.
 *
 * Auth model: every backend call presents a DIME JWT minted through the
 * host-supplied `DimeTokenExchange` (Clerk today; see `identity.ts`). The DIME
 * JWT has a 5-minute TTL and there is no refresh endpoint, so each call here
 * mints a fresh one immediately before the request (and re-mints + retries
 * exactly once on a 401). Nothing returned by this module may be persisted —
 * connection grants are memory-only by contract (`Cache-Control: no-store`).
 *
 * Never log the access code, the DIME JWT, or the grant token.
 */
import {
  DimeApiError,
  type DimeIdentity,
  type DimeTokenExchange,
} from './identity.js';
import { resolveDimeApiUrl, type DimeEnvironment } from './environments.js';
import {
  createDimeSessionCache,
  sessionCacheKey,
  type DimeSessionCache,
} from './sessionCache.js';

/**
 * Sidecar forward auth for `TerminalConfig.sidecar`: routes the model-catalog
 * and session-sync calls through the web-api's allowlisted `/v1/gateway/*`
 * forward, authenticated by a fresh-enough DIME JWT — the backend injects
 * the real gateway credential, so the browser never needs the gateway URL or
 * token for sidecar calls.
 */
export interface SidecarAuth {
  readonly baseUrl: string;
  readonly getAuthHeaders: () => Readonly<Record<string, string>> | null;
}

/**
 * The in-memory connection grant: everything the terminal needs to reach the
 * user's gateway. `token` is the grant's gateway token when present, otherwise
 * the DIME JWT that resolved the connection (per the /v1/connection contract).
 */
export interface ConnectionGrant {
  readonly gatewayUrl: string;
  readonly token: string;
  /**
   * True when `token` is the DIME JWT itself (gateway-proxy grant with no
   * gateway token). Such a token expires in 5 minutes, so the terminal must
   * mint a fresh one per handshake (`resolveHandshakeToken`) instead of
   * replaying this one on reconnect. False ⇒ `token` is a long-lived gateway
   * credential and must be presented as-is.
   */
  readonly usesDimeJwt: boolean;
}

export type ConnectionResolution =
  | { readonly kind: 'connected'; readonly grant: ConnectionGrant }
  /** 202 — gateway still provisioning; poll again in ~2s. */
  | { readonly kind: 'provisioning' }
  /** 404 — JWT said onboarded but the account is gone; show the code screen. */
  | { readonly kind: 'no-account' }
  /** 409 — failed/suspended; terminal error screen. */
  | { readonly kind: 'unavailable'; readonly status: string };

export type RedeemResolution =
  /**
   * 201 created, or 409 account-already-exists (treated as success). Carries
   * the identity that authorized the redeem so the follow-up connection
   * resolve can reuse it instead of re-exchanging.
   */
  | { readonly kind: 'redeemed'; readonly identity: DimeIdentity }
  /** 400/403 — bad code; show inline and let the user retry. */
  | { readonly kind: 'rejected'; readonly message: string };

export interface DimeCloudOptions {
  /** Named backend to target. Default `'production'`. */
  readonly environment?: DimeEnvironment;
  /** Explicit web-api base URL — wins over `environment`. */
  readonly apiUrl?: string;
  /** How this host obtains DIME JWTs (e.g. `createClerkTokenExchange(getToken)`). */
  readonly exchange: DimeTokenExchange;
  /**
   * Provider-held session cache (`DimeTerminalProvider`), so minted
   * identities and resolved grants survive surface remounts. Omit ⇒ a
   * private per-client cache (per-mount lifetime, the historical behavior).
   */
  readonly sessionCache?: DimeSessionCache;
  /** Account id scoping the cache key; identities never cross accounts. */
  readonly accountId?: string;
}

export interface DimeCloudClient {
  /** The resolved web-api base URL this client targets (no trailing slash). */
  readonly apiUrl: string;
  /**
   * The DIME identity for this session — cache-first (single-flight, reused
   * while >60s of life remains), minting via the host's exchange on a miss.
   */
  readonly mintIdentity: () => Promise<DimeIdentity>;
  /**
   * Key-bound view of the session cache's grant slot, for the connection
   * state machine: a remount rehydrates from a still-fresh grant instead of
   * re-resolving (and re-minting).
   */
  readonly grantCache: {
    readonly get: () => ConnectionGrant | null;
    readonly invalidate: () => void;
  };
  /**
   * GET /v1/connection — resolve how this user's client connects. Pass a
   * just-minted `identity` to skip re-exchanging for the first attempt.
   */
  readonly fetchConnection: (
    preMinted?: DimeIdentity,
  ) => Promise<ConnectionResolution>;
  /** POST /v1/account — redeem a one-time operator-issued access code. */
  readonly redeemAccessCode: (code: string) => Promise<RedeemResolution>;
  /**
   * Sidecar auth over the shared session cache: `getAuthHeaders` returns the
   * cached token while fresh, or null while a background mint fills the cache
   * (safe: session sync treats null as transient and retries, and the model
   * fetch falls back to the static catalog).
   */
  readonly createSidecarAuth: () => SidecarAuth;
  /**
   * Fresh-enough-per-handshake DIME JWT for
   * `TerminalConfig.resolveHandshakeToken` (gateway-proxy grants only — the
   * proxy verifies the JWT at every connect). Rides the shared session cache
   * (reuse while >60s left, single-flight); resolves null on a mint failure
   * so the terminal falls back to its stored credential (transient, not
   * fatal).
   */
  readonly createHandshakeTokenResolver: () => () => Promise<string | null>;
}

export function createDimeCloudClient(opts: DimeCloudOptions): DimeCloudClient {
  const apiUrl = resolveDimeApiUrl(opts);
  const { exchange } = opts;
  const cache = opts.sessionCache ?? createDimeSessionCache();
  const cacheKey = sessionCacheKey(apiUrl, opts.accountId ?? '');

  const mintIdentity = (): Promise<DimeIdentity> =>
    cache.getIdentity(cacheKey, () => exchange(apiUrl));

  /**
   * Call a DIME endpoint with a just-minted DIME JWT; on 401 (JWT rejected —
   * e.g. clock skew ate the 5-minute TTL) re-exchange and retry exactly once.
   * Returns the response plus the identity whose JWT authorized it, so callers
   * that need the JWT as a fallback gateway token get the one that worked.
   *
   * `identity` lets a caller that just minted one (e.g. the onboarding-check
   * exchange) reuse it for the first attempt instead of minting again; the
   * 401 retry always re-mints, so a stale hand-me-down still self-heals.
   */
  const authedFetch = async (
    path: string,
    init: Omit<RequestInit, 'headers'> & {
      readonly headers?: Record<string, string>;
    } = {},
    identity?: DimeIdentity,
  ): Promise<{ readonly res: Response; readonly identity: DimeIdentity }> => {
    const attempt = async (
      preMinted?: DimeIdentity,
    ): Promise<{
      res: Response;
      identity: DimeIdentity;
    }> => {
      const id = preMinted ?? (await mintIdentity());
      const res = await fetch(`${apiUrl}${path}`, {
        ...init,
        headers: {
          ...init.headers,
          Authorization: `Bearer ${id.token}`,
        },
      });
      return { res, identity: id };
    };

    const first = await attempt(identity);
    if (first.res.status !== 401) return first;
    // The JWT was rejected — a cached identity is poisoned; drop it so the
    // retry (and every other consumer) mints fresh.
    cache.invalidateIdentity(cacheKey);
    return attempt();
  };

  const fetchConnection = async (
    preMinted?: DimeIdentity,
  ): Promise<ConnectionResolution> => {
    const { res, identity } = await authedFetch(
      '/v1/connection',
      {},
      preMinted,
    );
    if (res.status === 200) {
      const body = (await res.json()) as { url?: string; token?: string };
      if (typeof body.url !== 'string' || body.url.length === 0) {
        throw new DimeApiError(
          200,
          'Connection grant is missing a gateway URL',
        );
      }
      // Contract: when the grant carries no gateway token, the client
      // presents the DIME JWT itself to the gateway.
      const hasGatewayToken =
        typeof body.token === 'string' && body.token.length > 0;
      const grant: ConnectionGrant = {
        gatewayUrl: body.url,
        token: hasGatewayToken ? (body.token as string) : identity.token,
        usesDimeJwt: !hasGatewayToken,
      };
      cache.setGrant(cacheKey, grant);
      return { kind: 'connected', grant };
    }
    // Any non-connected resolution supersedes a previously cached grant —
    // a remount must re-resolve, not rehydrate a stale positive.
    cache.invalidateGrant(cacheKey);
    if (res.status === 202) return { kind: 'provisioning' };
    if (res.status === 404) return { kind: 'no-account' };
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as {
        status?: string;
      } | null;
      return { kind: 'unavailable', status: body?.status ?? 'failed' };
    }
    throw new DimeApiError(
      res.status,
      `Connection resolve failed (${res.status})`,
    );
  };

  const redeemAccessCode = async (code: string): Promise<RedeemResolution> => {
    const { res, identity } = await authedFetch('/v1/account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (res.status === 201 || res.status === 409) {
      // The cached identity (if any) predates onboarding — its `onboarded`
      // claim is now stale. The redeem's own identity is threaded to the
      // follow-up resolve, so this costs nothing on the happy path.
      cache.invalidateIdentity(cacheKey);
      return { kind: 'redeemed', identity };
    }
    if (res.status === 403) {
      return {
        kind: 'rejected',
        message: 'Invalid access code — check it and try again.',
      };
    }
    if (res.status === 400) {
      return {
        kind: 'rejected',
        message: 'That access code can’t be used — check it and try again.',
      };
    }
    throw new DimeApiError(
      res.status,
      `Access-code redeem failed (${res.status})`,
    );
  };

  // Both token consumers ride the shared session cache — freshness slack,
  // single-flighting and cross-consumer reuse all live there. No private
  // caches, no eager mint: the bootstrap/silent exchange has usually
  // populated the cache before either consumer first asks.
  const createSidecarAuth = (): SidecarAuth => ({
    baseUrl: `${apiUrl}/v1/gateway`,
    getAuthHeaders: () => {
      const token = cache.peekToken(cacheKey);
      if (token == null) {
        // Sync contract: null now (session sync treats it as transient and
        // retries; the model fetch falls back to the static catalog) while a
        // background mint fills the cache for the next call.
        void mintIdentity().catch(() => {});
        return null;
      }
      return { Authorization: `Bearer ${token}` };
    },
  });

  const createHandshakeTokenResolver =
    (): (() => Promise<string | null>) => () =>
      mintIdentity()
        .then((id) => id.token)
        // Null on a mint failure so the terminal falls back to its stored
        // credential (transient, not fatal).
        .catch(() => null);

  return {
    apiUrl,
    mintIdentity,
    grantCache: {
      get: () => cache.getGrant(cacheKey),
      invalidate: () => cache.invalidateGrant(cacheKey),
    },
    fetchConnection,
    redeemAccessCode,
    createSidecarAuth,
    createHandshakeTokenResolver,
  };
}
